import { useMemo } from 'react';
import { css } from '@emotion/react';
import { InvoiceFilterType } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { SwitchChipGroup, Text, useSwitchChipGroup } from '@frontend/design-system';
import { calculateRange } from '../../../utils/date-helpers';
import { kebabCase } from '../../../utils/helpers';
import { quickRangeConfig } from '../../QuickFill/quickRange.config';
import { CustomDateFilterChip } from './custom-date-filter-chip';

const styles = {
  container: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(3)};
    .payments-filter-chips-group.selected {
      background-color: ${theme.colors.primary10};
      color: ${theme.colors.white};
      border: solid 1px ${theme.colors.primary50};
    }
    @media (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(0, 2)};
    }
  `,
  switchChipGroup: css`
    @media (max-width: ${breakpoints.medium.max}px) {
      flex-wrap: wrap;
    }
  `,
};

export type DateFiltersProps = {
  dateRange: InvoiceFilterType['dateRange'];
  onChangeDateRange?: (dateRange: InvoiceFilterType['dateRange']) => void;
};

export const DateFilters = ({ dateRange, onChangeDateRange }: DateFiltersProps) => {
  const { t } = useTranslation('payments');

  const dateOptions = useMemo(() => quickRangeConfig(t, true, true), []);

  const selectedValue = useMemo(() => {
    if (!dateRange?.start || !dateRange?.end) return 'All Time';
    const selected = dateOptions.find((option) => {
      const [start, end] = calculateRange(option);
      return start === dateRange?.start && end === dateRange?.end;
    });
    return selected?.label ?? 'Custom';
  }, [dateRange?.start, dateRange?.end, dateOptions]);

  const props = useSwitchChipGroup({
    onChange: (val) => {
      const selectedOption = dateOptions.find((option) => option.label === val[0]);
      if (selectedOption && selectedOption.period !== 'custom') {
        const [start, end] = calculateRange(selectedOption);
        onChangeDateRange?.({ start, end });
      }
    },
    defaultValue: [selectedValue],
  });

  const handleCustomSelect = (range: string[]) => {
    if (onChangeDateRange) {
      const [start, end] = range;
      onChangeDateRange({ start, end });
    }
    props.setValue(['Custom']);
  };

  return (
    <section>
      <div data-trackingid='pay-portal-invoices-quickfillbar' css={styles.container}>
        <SwitchChipGroup singleSelect {...props} css={styles.switchChipGroup}>
          {dateOptions
            .filter((o) => o.period !== 'custom')
            .map((option) => (
              <SwitchChipGroup.Option
                key={option.label}
                as='button'
                value={option.label}
                className={`payments-filter-chips-group ${props.value.includes(option.label) ? 'selected' : ''}`}
                trackingId={`pay-portal-invoicelist-quick-range-btn-${kebabCase(option.label)}`}
              >
                <Text size='medium'>{option.label}</Text>
              </SwitchChipGroup.Option>
            ))}
          <CustomDateFilterChip
            onSelect={handleCustomSelect}
            isSelected={props.value.includes('Custom')}
            dateRange={dateRange}
          />
        </SwitchChipGroup>
      </div>
    </section>
  );
};
